import AuthService from './AuthService';

class TicketService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || '';
        this.apiBaseUrl = rawBaseUrl.replace(/\/+$/, '');
        this.ticketsPath = '/private/tickets';
        this.supportTicketsPath = '/private/support-tickets';
    }

    getAdminTicketsPath(clientId) {
        if (!clientId) throw new Error('clientId is required');
        return `/admin/clients/${encodeURIComponent(clientId)}/tickets`;
    }

    getAuthHeaders(extraHeaders) {
        const token = AuthService.getAccessToken();
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        return {
            ...authHeader,
            ...(extraHeaders || {}),
        };
    }

    async getPaginatedList(path, { page = 1, pageSize = 20 } = {}) {
        const query = new URLSearchParams({ page, pageSize });
        const url = `${this.apiBaseUrl}${path}?${query.toString()}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return response.json();
    }

    async getTickets(params = {}) {
        return this.getPaginatedList(this.ticketsPath, params);
    }

    async getSupportTickets(params = {}) {
        return this.getPaginatedList(this.supportTicketsPath, params);
    }

    async getSupportTicket(supportTicketId) {
        if (!supportTicketId) throw new Error('supportTicketId is required');
        const url = `${this.apiBaseUrl}${this.supportTicketsPath}/${encodeURIComponent(supportTicketId)}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async addSupportTicketNote(supportTicketId, body, visibleToCustomer = false) {
        if (!supportTicketId) throw new Error('supportTicketId is required');
        const url = `${this.apiBaseUrl}${this.supportTicketsPath}/${encodeURIComponent(supportTicketId)}/notes`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ body, visibleToCustomer }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async updateSupportTicket(supportTicketId, payload) {
        if (!supportTicketId) throw new Error('supportTicketId is required');
        const url = `${this.apiBaseUrl}${this.supportTicketsPath}/${encodeURIComponent(supportTicketId)}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async uploadSupportTicketAttachment(supportTicketId, file, onProgress) {
        if (!supportTicketId) throw new Error('supportTicketId is required');
        if (!file) throw new Error('file is required');
        const url = `${this.apiBaseUrl}${this.supportTicketsPath}/${encodeURIComponent(supportTicketId)}/attachments`;
        return this.uploadAttachmentRequest(url, file, onProgress);
    }

    async getPublicSupportTicket(accessToken) {
        if (!accessToken) throw new Error('accessToken is required');
        const url = `${this.apiBaseUrl}/support-tickets/${encodeURIComponent(accessToken)}`;
        const response = await fetch(url, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async addPublicSupportTicketNote(accessToken, body) {
        if (!accessToken) throw new Error('accessToken is required');
        const url = `${this.apiBaseUrl}/support-tickets/${encodeURIComponent(accessToken)}/notes`;
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ body }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    uploadPublicAttachmentRequest(url, file, onProgress) {
        const formData = new FormData();
        formData.append('file', file);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url);

            xhr.upload.onprogress = (event) => {
                if (!event.lengthComputable || typeof onProgress !== 'function') return;
                onProgress(Math.min(95, Math.round((event.loaded / event.total) * 100)));
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        resolve(JSON.parse(xhr.responseText || '{}'));
                    } catch (e) {
                        reject(new Error('Invalid support ticket attachment response'));
                    }
                    return;
                }
                reject(new Error(`HTTP error! Status: ${xhr.status}`));
            };

            xhr.onerror = () => reject(new Error('Network error while uploading attachment'));
            xhr.onabort = () => reject(new Error('Attachment upload was cancelled'));

            xhr.send(formData);
        });
    }

    async uploadPublicSupportTicketAttachment(accessToken, file, onProgress) {
        if (!accessToken) throw new Error('accessToken is required');
        if (!file) throw new Error('file is required');
        const url = `${this.apiBaseUrl}/support-tickets/${encodeURIComponent(accessToken)}/attachments`;
        return this.uploadPublicAttachmentRequest(url, file, onProgress);
    }

    async createTicket(payload) {
        const url = `${this.apiBaseUrl}${this.ticketsPath}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async getTicket(ticketId) {
        if (!ticketId) throw new Error('ticketId is required');
        const url = `${this.apiBaseUrl}${this.ticketsPath}/${encodeURIComponent(ticketId)}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async updateTicket(ticketId, payload) {
        if (!ticketId) throw new Error('ticketId is required');
        const url = `${this.apiBaseUrl}${this.ticketsPath}/${encodeURIComponent(ticketId)}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    uploadAttachmentRequest(url, file, onProgress) {
        const formData = new FormData();
        formData.append('file', file);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url);

            Object.entries(this.getAuthHeaders()).forEach(([key, value]) => {
                xhr.setRequestHeader(key, value);
            });

            xhr.upload.onprogress = (event) => {
                if (!event.lengthComputable || typeof onProgress !== 'function') return;
                onProgress(Math.min(95, Math.round((event.loaded / event.total) * 100)));
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        resolve(JSON.parse(xhr.responseText || '{}'));
                    } catch (e) {
                        reject(new Error('Invalid ticket attachment response'));
                    }
                    return;
                }
                reject(new Error(`HTTP error! Status: ${xhr.status}`));
            };

            xhr.onerror = () => reject(new Error('Network error while uploading attachment'));
            xhr.onabort = () => reject(new Error('Attachment upload was cancelled'));

            xhr.send(formData);
        });
    }

    async uploadTicketAttachment(ticketId, file, onProgress) {
        if (!ticketId) throw new Error('ticketId is required');
        if (!file) throw new Error('file is required');
        const url = `${this.apiBaseUrl}${this.ticketsPath}/${encodeURIComponent(ticketId)}/attachments`;
        return this.uploadAttachmentRequest(url, file, onProgress);
    }

    async deleteTicketAttachment(ticketId, fileId) {
        if (!ticketId) throw new Error('ticketId is required');
        if (!fileId) throw new Error('fileId is required');
        const url = `${this.apiBaseUrl}${this.ticketsPath}/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(fileId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async addTicketNote(ticketId, body) {
        if (!ticketId) throw new Error('ticketId is required');
        const url = `${this.apiBaseUrl}${this.ticketsPath}/${encodeURIComponent(ticketId)}/notes`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ body }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async getAdminTickets(clientId, params = {}) {
        return this.getPaginatedList(this.getAdminTicketsPath(clientId), params);
    }

    async createAdminTicket(clientId, payload) {
        const url = `${this.apiBaseUrl}${this.getAdminTicketsPath(clientId)}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async getAdminTicket(clientId, ticketId) {
        if (!ticketId) throw new Error('ticketId is required');
        const url = `${this.apiBaseUrl}${this.getAdminTicketsPath(clientId)}/${encodeURIComponent(ticketId)}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async updateAdminTicket(clientId, ticketId, payload) {
        if (!ticketId) throw new Error('ticketId is required');
        const url = `${this.apiBaseUrl}${this.getAdminTicketsPath(clientId)}/${encodeURIComponent(ticketId)}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async uploadAdminTicketAttachment(clientId, ticketId, file, onProgress) {
        if (!ticketId) throw new Error('ticketId is required');
        if (!file) throw new Error('file is required');
        const url = `${this.apiBaseUrl}${this.getAdminTicketsPath(clientId)}/${encodeURIComponent(ticketId)}/attachments`;
        return this.uploadAttachmentRequest(url, file, onProgress);
    }

    async deleteAdminTicketAttachment(clientId, ticketId, fileId) {
        if (!ticketId) throw new Error('ticketId is required');
        if (!fileId) throw new Error('fileId is required');
        const url = `${this.apiBaseUrl}${this.getAdminTicketsPath(clientId)}/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(fileId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }

    async addAdminTicketNote(clientId, ticketId, body) {
        if (!ticketId) throw new Error('ticketId is required');
        const url = `${this.apiBaseUrl}${this.getAdminTicketsPath(clientId)}/${encodeURIComponent(ticketId)}/notes`;
        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ body }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    }
}

const ticketService = new TicketService();
export { TicketService };
export default ticketService;
